/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {createPoint, createPolygon, createPolyline} from "@luciad/ria/shape/ShapeFactory";
import {PointLabelPosition} from "@luciad/ria/view/style/PointLabelPosition";
import {IconFactory} from "@luciad/ria-sample-common/IconFactory";
import { FormatUtil } from "@luciad/ria-sample-common/FormatUtil";
import { createTransformation} from "@luciad/ria/transformation/TransformationFactory";
import { getReference } from "@luciad/ria/reference/ReferenceProvider";

const RULER_COLOR = "rgb(255,0,0)";
const HELPER_COLOR = "rgb(255,255,0)";
const HTML_TEMPLATE_PATTERN = "__CONTENT__";
const HTML_TEMPLATE_REGEX = new RegExp(HTML_TEMPLATE_PATTERN, 'g');

const style = {
  lineStyle: {
    stroke: {
      color: RULER_COLOR,
      width: 5
    }
  },
  helperLineStyle: {
    stroke: {
      color: HELPER_COLOR,
      width: 1
    }
  },
  iconStyle: createIconStyle(RULER_COLOR),
  labelStyle: {
    group: "ruler3DLabel",
    padding: 2,
    priority: -100,
    positions: [PointLabelPosition.ABOVE]
  },
  textHtmlTemplate: createHtmlTemplate(RULER_COLOR),
  helperTextHtmlTemplate: createHtmlTemplate(HELPER_COLOR, "rgb(0,0,0)"),
  areaStyle: {
    fill: {
      color: "rgba(255,200,0,0.2)"
    },
    stroke: {
      color: "rgba(0,0,0,0)",
      width: 1
    }
  }
};

function createIconStyle(color) {
  const iconSize = 17;
  return {
    image: IconFactory.circle({
      stroke: color,
      fill: color,
      width: iconSize,
      height: iconSize
    }),
    width: iconSize + "px",
    height: iconSize + "px"
  }
}

function createTextShadowHalo(color) {
  return '1px 1px ' + color + ', 1px -1px ' + color + ', -1px 1px ' + color + ', -1px -1px ' + color + ';'
}

function createHtmlTemplate(haloColor, textColor) {
  textColor = textColor || "rgb(255,255,255)";
  haloColor = haloColor || "rgb(0,0,0)";
  return '<div style=\'' +
    'font: bold 14px sans-serif;' +
    'color:' + textColor + ';' +
    'text-shadow:' + createTextShadowHalo(haloColor) +
    '\'>' + HTML_TEMPLATE_PATTERN + '</div>';
}

/**
 * Factory function creating Ruler 3D Presentation object
 * @param geoContext {geodesy, modelToWorldTx, worldToModelTx} contains utilities for geo calculations
 */
function createPresentation(geoContext) {
  const geodesy = geoContext.geodesy;
  const modelToWorldTx = geoContext.modelToWorldTx;

  function drawSegmentPoints(geoCanvas, segment, i) {
    if (i === 0) {
      geoCanvas.drawIcon(segment.p1.modelPoint, style.iconStyle);
    }

    geoCanvas.drawIcon(segment.p2.modelPoint, style.iconStyle);
  }

  function drawSegment(geoCanvas, segment) {
    geoCanvas.drawShape(segment.line, style.lineStyle);
  }

  function drawSegmentLabel(labelCanvas, segment) {
    if (segment.distance) {
      const text = FormatUtil.distance(segment.distance);
      const html = style.textHtmlTemplate.replace(HTML_TEMPLATE_REGEX, text);
      labelCanvas.drawLabel(html, segment.line, style.labelStyle);
    }
  }

  function drawOrtho(geoCanvas, segment) {

    const orthoInfo = memoOrthogonal(segment);

    if (orthoInfo.distanceH > 1 && orthoInfo.distanceV > 1) {
      geoCanvas.drawShape(orthoInfo.area, style.areaStyle);

      geoCanvas.drawShape(orthoInfo.lineH, style.helperLineStyle);
      geoCanvas.drawShape(orthoInfo.lineV, style.helperLineStyle);
    }
  }

  function drawOrthoLabel(labelCanvas, segment) {
    const orthoInfo = memoOrthogonal(segment);

    if (orthoInfo.distanceH > 1 && orthoInfo.distanceV > 1) {
      const lineHHtml = style.helperTextHtmlTemplate.replace(HTML_TEMPLATE_REGEX, FormatUtil.distance(orthoInfo.distanceH));
      labelCanvas.drawLabel(lineHHtml, orthoInfo.lineH, style.labelStyle);
      const lineVHtml = style.helperTextHtmlTemplate.replace(HTML_TEMPLATE_REGEX, FormatUtil.distance(orthoInfo.distanceV));
      labelCanvas.drawLabel(lineVHtml, orthoInfo.lineV, style.labelStyle);
      const angleHtml = style.helperTextHtmlTemplate.replace(HTML_TEMPLATE_REGEX, FormatUtil.angle(orthoInfo.angle));
      labelCanvas.drawLabel(angleHtml, segment.p1.modelPoint, style.labelStyle);
    }
  }

  function memoOrthogonal(segment) {

    if (segment.orthogonal && segment.isFinal) {
      return segment.orthogonal;
    }
    const p1 = segment.p1.modelPoint;
    const p2 = segment.p2.modelPoint;
    const ref = p1.reference;

    const p90Model = createPoint(ref, [p2.x, p2.y, p1.z]);

    const p90World = modelToWorldTx.transform(p90Model);
    const dH = calculateDistance(segment.p1.worldPoint, p90World);
    const dV = calculateDistance(segment.p2.worldPoint, p90World);
    const angle = calculateAngle(dV, segment.distance);

    const orthogonal = {
      lineH: createPolyline(ref, [p1, p90Model]),
      lineV: createPolyline(ref, [p2, p90Model]),
      area: createPolygon(ref, [p1, p2, p90Model]),
      distanceH: dH,
      distanceV: dV,
      angle: angle
    };
    segment.orthogonal = orthogonal;
    return orthogonal;
  }

  function drawHeight(geoCanvas, segment, i) {
    if (i === 0) {
      drawHeightLine(geoCanvas, segment.p1.modelPoint);
    }
    drawHeightLine(geoCanvas, segment.p2.modelPoint);
  }

  function drawHeightLabel(labelCanvas, segment, i) {
    if (i === 0) {
      drawHeightLineLabel(labelCanvas, segment.p1.modelPoint);
    }
    drawHeightLineLabel(labelCanvas, segment.p2.modelPoint);
  }

  function drawHeightLine(geoCanvas, modelPoint) {
    const ref = modelPoint.reference;
    const pGround = createPoint(ref, [modelPoint.x, modelPoint.y, 0]);
    const verticalLine = createPolyline(ref, [pGround, modelPoint]);

    geoCanvas.drawShape(verticalLine, style.helperLineStyle);
  }

  const wgsToKrovak = createTransformation(getReference('EPSG:4326'), getReference('EPSG:5514'));

  function getZInKrovak(point) {
    return wgsToKrovak.transform(point).z;
  }

  function drawHeightLineLabel(labelCanvas, modelPoint) {
    // const heightHtml = style.textHtmlTemplate.replace(HTML_TEMPLATE_PATTERN, "H: " + FormatUtil.height(modelPoint.z));
    const heightHtml = style.textHtmlTemplate.replace(HTML_TEMPLATE_PATTERN, "H: " + FormatUtil.height(getZInKrovak(modelPoint)));
    labelCanvas.drawLabel(heightHtml, modelPoint, style.labelStyle);
  }

  function drawArea(geoCanvas, segments) {
    if (segments.length === 0) {
      return;
    }

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const p0 = segments[0].p1;
      const areaInfo = memoArea(segment, p0);
      if (areaInfo.shape) {
        geoCanvas.drawShape(areaInfo.shape, style.areaStyle);
      }
    }
  }

  function drawAreaLabel(labelCanvas, segments) {
    if (segments.length === 0) {
      return;
    }

    const p0 = segments[0].p1;
    let areaSum = 0;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      areaSum += segment.area;
    }

    const areaHtml = style.helperTextHtmlTemplate.replace(HTML_TEMPLATE_PATTERN, FormatUtil.area(areaSum));
    labelCanvas.drawLabel(areaHtml, p0.modelPoint, style.labelStyle);
  }

  function memoArea(segment, p0) {
    if (segment.areaInfo && segment.isFinal) {
      return segment.areaInfo;
    }
    let areaInfo = {
      shape: null,
      area: 0
    };

    const p1 = segment.p1;
    const p2 = segment.p2;
    if (p0.modelPoint !== p1.modelPoint) {
      const triplet = [p0, p1, p2];
      areaInfo = {
        shape: tripletToPolygon(triplet),
        area: tripletToArea(triplet)
      }
    }

    segment.areaInfo = areaInfo;
    return areaInfo;

    function tripletToPolygon(triplet) {
      const modelPoints = triplet.map(toModelPoint);
      const ref = modelPoints[0].reference;
      return createPolygon(ref, modelPoints);
    }

    function tripletToArea(triplet) {
      const wPoints = triplet.map(toWorldPoint);
      return calculateArea(
        calculateDistance(wPoints[0], wPoints[1]),
        calculateDistance(wPoints[0], wPoints[2]),
        calculateDistance(wPoints[1], wPoints[2])
      );
    }
  }

  function getTotals(segments) {

    return segments.reduce(function(acc, segment) {
      acc.length += segment.distance;
      acc.area += segment.areaInfo ? segment.areaInfo.area : 0;
      return acc;
    }, {
      length: 0,
      area: 0
    });
  }

  function calculateDistance(wp1, wp2) {
    return geodesy.distance3D(wp1, wp2);
  }

  function toModelPoint(point) {
    return point.modelPoint;
  }

  function toWorldPoint(point) {
    return point.worldPoint;
  }

  function calculateAngle(opposite, hypotenuse) {
    const radians = Math.asin(opposite / hypotenuse);
    return radiansToDegrees(radians);
  }

  function radiansToDegrees(radians) {
    return radians * (180 / Math.PI);
  }

  function calculateArea(a, b, c) {
    // based on  Heron's Formula for the area of a triangle
    const p = (a + b + c) / 2;
    return Math.sqrt(p * (p - a) * (p - b) * (p - c));
  }

  return {
    getTotals: getTotals,
    drawSegmentPoints: drawSegmentPoints,
    drawSegment: drawSegment,
    drawSegmentLabel: drawSegmentLabel,
    drawOrtho: drawOrtho,
    drawOrthoLabel: drawOrthoLabel,
    drawHeight: drawHeight,
    drawHeightLabel: drawHeightLabel,
    drawArea: drawArea,
    drawAreaLabel: drawAreaLabel
  };
}

export const Ruler3DPresentation = {
  createPresentation: createPresentation
};

